import React, { useEffect, useState } from 'react'
import { Row, Select } from 'antd'
import styled from 'styled-components'
import {
    AMAModelConfig,
    CombinedObject,
    customObjects,
    isCamera,
    ModelConfig,
    utilsEditor,
    utilsMaterial
} from '@amaspace-editor/editor-3d'
import { ActionTypeId, ProductActionTypeId } from '../../pages/assets/editor/logic/LogicEditor'
import TAsset from '../../types/TAsset'
import * as THREE from 'three'
import {
    ApplyRulesType,
    CatalogPreviewItemType,
    CatalogShareApplyRulesBody,
    CatalogShareResponse,
    ProductAttributeConnectionOptions,
    ProductAttributeResponse,
    ProductRuleAction,
    useApplyRulesShareMutation,
    useGetProductShareMutation
} from '../../api/shareApi'

type TSelectedProduct = CatalogPreviewItemType & { parent: number }

type Option = {
    label: string
    value: string
}

type AttributeSelectProps = {
    label: string
    options: Option[]
    onChange: (value: string) => void
    enabled: boolean
    visible: boolean
}

type Props = {
    accessToken?: string
    allProducts: CatalogPreviewItemType[]
    product: CatalogPreviewItemType
    setAllProducts: React.Dispatch<React.SetStateAction<CatalogPreviewItemType[]>>
}
type ProductAndAddParentType = Omit<CatalogShareResponse, 'product'> & {
    product: CatalogShareResponse['product'] & {
        parent: number
    }
}

const AttributeSelect = ({ label, onChange, enabled, visible, options }: AttributeSelectProps) => {
    return (
        <StyledSelectWrapper visible={visible}>
            <span>{label}</span>
            <Select disabled={!enabled} options={options} onChange={onChange} placeholder="Select..." />
        </StyledSelectWrapper>
    )
}

const ProductAttributesSelect = ({ allProducts, product, setAllProducts, accessToken }: Props) => {
    const [stateAttr, setStateAttr] = useState<ProductAttributeResponse[]>([])
    const [activeAttrValue, setActiveAttrValue] = useState<Record<string, number>>({})

    const [getProduct] = useGetProductShareMutation()
    const [applyRules] = useApplyRulesShareMutation()

    const editor = utilsEditor()
    const materialEdit = utilsMaterial()
    const customItems = customObjects()

    const validAttrs = (attrs: any, parent: any) => {
        return [...attrs].map(item => {
            return {
                ...item,
                parent: parent.id,
                updated: {
                    visible: true,
                    enabled: true,
                    value: '',
                    options: []
                }
            }
        })
    }

    const getProductAndAddParentToIt = async (
        id: string,
        parentID: number
    ): Promise<ProductAndAddParentType | undefined> => {
        if (!accessToken) return
        const body = {
            domain: 'localhost',
            accessToken: accessToken,
            product_id: id
        }
        const res = await getProduct(body)
        console.log(res)
        if ('error' in res) return
        return {
            ...res.data,
            product: {
                ...res.data.product,
                parent: parentID
            }
        }
    }

    const updateStateUploadedModels = (attributeID: number[]) => {
        const dataInstance: ModelConfig[] = [...editor.uploadedModelsConfig]

        const removeRecursively = (IDs: number[], arrayModels: ModelConfig[]): ModelConfig[] => {
            const newIDs: number[] = []
            const newArray = arrayModels
                .map(item => {
                    if (
                        item.additionalOptions.relatedAttribute &&
                        item.additionalOptions.relatedChildrenAttributes &&
                        IDs.includes(item.additionalOptions.relatedAttribute)
                    ) {
                        newIDs.push(...item.additionalOptions.relatedChildrenAttributes)
                        return null
                    }
                    return item
                })
                .filter((element): element is ModelConfig => element !== null)
            if (!newIDs.length) {
                return newArray.filter(element => element)
            }
            return removeRecursively(
                newIDs,
                newArray.filter(element => element)
            )
        }
        const result = removeRecursively(attributeID, dataInstance)
        editor.setUploadedModelsConfig(result)
    }

    const checkActions = (
        config: ModelConfig,
        product: TSelectedProduct,
        asset: any,
        attribute: ProductAttributeResponse,
        actions: any[]
    ) => {
        const targetAsset: TAsset = asset.assets[0]
        for (let i = 0; i < actions.length; i++) {
            const action = actions[i]

            if (typeof action.value === 'string') return
            if (!action?.value?.attribute) return
            if (attribute.replacement_attribute_id === action.value.attribute.id) {
                if (action.type_action === ActionTypeId.SetVisibility) {
                    const data = action.value
                    const mesh = editor.getMeshById(data.modelPart.value)
                    const other = actions.filter(i => i.id !== action.id)

                    if (!mesh) return
                    mesh.visible = data.value
                    const ti = setInterval(() => {
                        const res: THREE.Object3D<THREE.Object3DEventMap>[] = []
                        mesh.traverse(i => {
                            if ('isMesh' in i) res.push(i)
                        })
                        const final = res.every(i => i.visible)
                        if (final) {
                            clearInterval(ti)
                            setTimeout(() => {
                                checkActions(config, product, asset, attribute, other)
                            }, 300)
                        }
                    }, 400)

                    break
                }
                if (action.type_action === ActionTypeId.SetActiveCamera) {
                    const data = action.value
                    const mesh = editor.getMeshById(data.camera.value)
                    const other = actions.filter(i => i.id !== action.id)

                    if (mesh) {
                        const currentConfig = editor.getCurrentConfigObj(mesh)
                        if (!isCamera(currentConfig)) return
                        editor.setDynamicCamera({
                            position: mesh.position,
                            target: currentConfig?.target?.position || new THREE.Vector3(0, 0, 0)
                        })
                    }
                    checkActions(config, product, asset, attribute, other)
                    break
                }
                if (action.type_action === ActionTypeId.SetMaterial) {
                    const material = materialEdit.createMaterialFromAsset(asset)
                    const node = editor.getMeshById(action.value.modelPart.patch.meshId)
                    const other = actions.filter(i => i.id !== action.id)

                    if (!node) return

                    editor.updateObjectData(node, { material })
                    editor.updateUploadedModelData(node, { material })
                    checkActions(config, product, asset, attribute, other)
                    break
                }
                if (action.type_action === ActionTypeId.SetModel) {
                    const data = structuredClone(asset)
                    const target = editor.getMeshById(action.value?.modelPart?.id) as
                        | Partial<CombinedObject>
                        | undefined
                    updateStateUploadedModels([attribute.id])
                    const other = actions.filter(i => i.id !== action.id)

                    if (!target) return

                    customItems.create
                        .model({
                            name: targetAsset.name,
                            modelId: targetAsset.id,
                            lazyLoad: true,
                            modelData: {
                                ...data,
                                relatedAttribute: product.parent,
                                relatedChildrenAttributes: product.attributes.map((attribute: any) => attribute.id)
                            }
                        })
                        .then(res => {
                            console.log('res')
                            checkActions(config, product, asset, attribute, other)
                        })
                    break
                }
            }
        }
    }

    const updateAllProducts = (product: TSelectedProduct, parentIDs: number[]) => {
        const dataInstance: TSelectedProduct[] = [...allProducts] as TSelectedProduct[]

        const removeRecursively = (IDs: number[], arrayProducts: TSelectedProduct[]): TSelectedProduct[] => {
            const newIDs: number[] = []
            const newArray: TSelectedProduct[] = arrayProducts
                .map((item: TSelectedProduct) => {
                    if (IDs.includes(item.parent)) {
                        newIDs.push(...item.attributes.map((attribute: any) => attribute.id))
                        return null
                    }
                    return item
                })
                .filter((element): element is TSelectedProduct => element !== null)
            if (!newIDs.length) {
                return newArray
            }
            return removeRecursively(newIDs, newArray)
        }
        const result = removeRecursively(parentIDs, dataInstance)
        setAllProducts([...result, product])
    }

    const updateProductAttributes = (
        product: TSelectedProduct,
        attribute: ProductAttributeResponse
    ): ProductAttributeResponse[] | undefined => {
        if (!product?.attributes) {
            return
        }
        const dataInstance = [...stateAttr]
        const removeRecursively = (id: number, arrayAttr: ProductAttributeResponse[]): ProductAttributeResponse[] => {
            let newID
            const newArray = [...arrayAttr]
                .map((item: any) => {
                    if (item.parent === id) {
                        newID = item.id
                        return null
                    }
                    return {
                        ...item
                    }
                })
                .filter((element: any) => element)
            if (!newID) {
                return newArray
            }
            return removeRecursively(newID, newArray)
        }
        const result = removeRecursively(attribute.id, dataInstance)

        setStateAttr([...result, ...validAttrs(product.attributes, attribute)])

        return [...result, ...validAttrs(product.attributes, attribute)]
    }

    const updateAttributes = async (
        id: string,
        attribute: ProductAttributeResponse,
        allAttrs?: ProductAttributeResponse[]
    ) => {
        if (!accessToken) return
        const applyRule: ApplyRulesType = {
            attribute_id: attribute.id,
            value: String(id)
        }
        const body: CatalogShareApplyRulesBody = {
            domain: 'localhost',
            accessToken: accessToken,
            product_id: product.id,
            attributes: [applyRule]
        }
        const res = await applyRules(body)

        if ('error' in res) return
        if (!allAttrs) return

        const resetAttr = allAttrs.map(item => {
            return {
                ...item,
                updated: { visible: true, enabled: true, value: '', options: [] }
            }
        })
        setActiveAttrValue(prevState => ({ ...prevState, [attribute.id]: false }))

        if (activeAttrValue[attribute.id]) {
            setStateAttr(resetAttr)
            return
        }
        let final: ProductAttributeResponse
        const newAttr: ProductRuleAction[] = []
        res.data.forEach(attr => {
            const currentAttr = allAttrs.find(i => i.id === attr.attribute_id)

            if (!currentAttr) return

            const diffAttr = allAttrs.filter(i => i.id !== attr.attribute_id)
            final = { ...currentAttr }

            let options: ProductAttributeConnectionOptions[] = []

            final.connections?.forEach(item => {
                const products = item.products || []
                options = [...options, ...products]
            })

            attr.actions.forEach(item => {
                if (item.type_action === ProductActionTypeId['SetAttributeVisible']) {
                    newAttr.push(item)
                    final.updated.visible = Boolean(JSON.parse(item.value))
                    setActiveAttrValue(prevState => ({ ...prevState, [attribute.id]: true }))
                }
                if (item.type_action === ProductActionTypeId['SetAttributeEnabled']) {
                    newAttr.push(item)
                    final.updated.enabled = Boolean(JSON.parse(item.value))
                    setActiveAttrValue(prevState => ({ ...prevState, [attribute.id]: true }))
                }
                if (item.type_action === ProductActionTypeId['SetAttributeValue']) {
                    newAttr.push(item)
                    options = options.filter(prod => prod.id === Number(item.value))
                    final.updated.options = options
                    setActiveAttrValue(prevState => ({ ...prevState, [attribute.id]: true }))
                }
                if (item.type_action === ProductActionTypeId['SetAttributeValueEnabled']) {
                    newAttr.push(item)
                    setActiveAttrValue(prevState => ({ ...prevState, [attribute.id]: true }))
                    options = options.map(prod => {
                        if (prod.id === Number(item.option_id)) {
                            return {
                                ...prod,
                                disabled: Boolean(JSON.parse(item.value))
                            }
                        }
                        return { ...prod }
                    })
                    final.updated.options = options
                }
                if (item.type_action === ProductActionTypeId['SetAttributeValueVisibility']) {
                    newAttr.push(item)
                    setActiveAttrValue(prevState => ({ ...prevState, [attribute.id]: true }))
                    options = options.filter(prod => prod.id !== Number(item.option_id))

                    final.updated.options = options
                }
            })

            setStateAttr([...diffAttr, final])
        })
        return newAttr
    }

    const changeAttribute = async (id: string, attribute: ProductAttributeResponse) => {
        const currentProduct = await getProductAndAddParentToIt(id, attribute.id)

        if (!currentProduct) return
        updateAllProducts(currentProduct.product, [attribute.id])
        const updatedAttrs = updateProductAttributes(currentProduct.product, attribute)
        const test = await updateAttributes(id, attribute, updatedAttrs)

        if (!attribute.replacement_attribute_id) return
        if (test) {
            await Promise.all(
                test.map(async act => {
                    const attr = stateAttr.find(i => i.id === act.attribute_id)
                    const currentProduct = await getProductAndAddParentToIt(act.value, attribute.id)
                    if (!currentProduct || !attr) return

                    checkActions(editor.config, currentProduct.product, currentProduct, attr, editor.config.actions)
                })
            )
        }
        checkActions(editor.config, currentProduct.product, currentProduct, attribute, editor.config.actions)

        // if (!editor.uploadedModelsConfig.length) return
        //
        // editor.uploadedModelsConfig.map(async config => {
        //     checkActions(config, currentProduct.product, currentProduct, attribute, editor.config.actions)
        //     if (test) {
        //         await Promise.all(
        //             test.map(async act => {
        //                 const attr = stateAttr.find(i => i.id === act.attribute_id)
        //                 const currentProduct = await getProductAndAddParentToIt(act.value, attribute.id)
        //                 if (!currentProduct || !attr) return
        //
        //                 checkActions(editor.config, currentProduct.product, currentProduct, attr, editor.config.actions)
        //             })
        //         )
        //     }
        // })
    }
    useEffect(() => {
        if (!product || !product.attributes) {
            return
        }
        const patchedAttrs: ProductAttributeResponse[] = product.attributes.map(attr => {
            const final: any = {
                ...attr,
                updated: {
                    visible: true,
                    enabled: true,
                    value: '',
                    options: []
                }
            }
            return final
        })
        setStateAttr(patchedAttrs)
    }, [product])
    return (
        <Container>
            {stateAttr.length &&
                stateAttr.map(attribute => {
                    if (attribute.type !== 'part_reference') return
                    let options: Option[] = []
                    attribute.connections &&
                        attribute.connections.forEach(connection => {
                            connection?.products?.forEach(product => {
                                options.push({ label: product.name, value: String(product.id) })
                            })
                        })
                    if (attribute.updated?.options?.length) {
                        options = [...attribute.updated.options].map(item => ({
                            label: item.name,
                            value: String(item.id),
                            disabled: item.disabled
                        }))
                    }
                    return (
                        <PanelRow key={attribute.id}>
                            <AttributeSelect
                                enabled={attribute.updated.enabled}
                                visible={attribute.updated.visible}
                                label={attribute.name}
                                options={options}
                                onChange={data => changeAttribute(data, attribute)}
                            />
                        </PanelRow>
                    )
                })}
        </Container>
    )
}

export default ProductAttributesSelect

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    max-width: 300px;
`

const PanelRow = styled(Row)`
    display: flex;
    flex-direction: column;
    gap: 5px;
`
const StyledSelectWrapper = styled.div<{ visible: boolean }>`
    display: ${props => (props?.visible ? 'flex' : 'none')};
    flex-direction: column;
`
